// A5 Kalkulator RAB (stage pelaksanaan_pemilihan): the offer's cost rows.
// Each row is uraian, volume, satuan and harga satuan; jumlah is volume x
// harga satuan, computed here as the user types and again by the API on
// save. Nothing is added silently: PPN counts only when a percentage is
// entered. The saved RAB downloads as XLSX or DOCX.
import { useEffect, useId, useState } from "react";
import * as API from "../../api";
import { formatRupiah } from "../../lib/format";
import { Field, Notice, SkeletonRows, useAsync } from "../../ui/components";
import { Glyph } from "../../ui/icons";
import * as Work from "./api";
import type { Rab } from "./api";
import { WorkSection, cents, localKey, parseNumber } from "./shared";

interface RowDraft {
  key: string;
  id?: string;
  uraian: string;
  volume: string;
  satuan: string;
  hargaSatuan: string;
}

const show = (n: number) => String(n).replace(".", ",");

function draftOf(rab: Rab): RowDraft[] {
  return rab.rows.map((r) => ({ key: r.id, id: r.id, uraian: r.uraian, volume: show(r.volume), satuan: r.satuan, hargaSatuan: show(r.hargaSatuan) }));
}

/** Subtotal, PPN and total of the draft (the API's formula). */
export function totalsOf(rows: { volume: number; hargaSatuan: number }[], ppnPercent: number | null) {
  const subtotal = cents(rows.reduce((sum, r) => sum + cents(r.volume * r.hargaSatuan), 0));
  const ppn = ppnPercent === null ? 0 : cents((subtotal * ppnPercent) / 100);
  return { subtotal, ppn, total: cents(subtotal + ppn) };
}

export function KalkulatorRab({ projectId }: { projectId: string }) {
  const uid = useId();
  const rab = useAsync<Rab>(() => Work.getRab(projectId), [projectId]);
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [ppn, setPpn] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rab.data) return;
    setRows(draftOf(rab.data));
    setPpn(rab.data.ppnPercent === null ? "" : show(rab.data.ppnPercent));
    setDirty(false);
  }, [rab.data]);

  const edit = (key: string, patch: Partial<RowDraft>) => {
    setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const parsed = rows.map((r) => ({ ...r, v: parseNumber(r.volume), h: parseNumber(r.hargaSatuan) }));
  const ppnValue = ppn.trim() ? parseNumber(ppn) : null;
  const ppnInvalid = ppnValue !== null && (Number.isNaN(ppnValue) || ppnValue < 0 || ppnValue > 100);
  const invalid = ppnInvalid || parsed.some((r) => !r.uraian.trim() || Number.isNaN(r.v) || r.v < 0 || Number.isNaN(r.h) || r.h < 0);
  const totals = totalsOf(
    parsed.map((r) => ({ volume: Number.isNaN(r.v) ? 0 : r.v, hargaSatuan: Number.isNaN(r.h) ? 0 : r.h })),
    ppnInvalid ? null : ppnValue,
  );

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      rab.set(
        await Work.saveRab(projectId, {
          rows: parsed.map((r) => ({ id: r.id, uraian: r.uraian.trim(), volume: r.v, satuan: r.satuan.trim(), hargaSatuan: r.h })),
          ppnPercent: ppnValue,
        }),
      );
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (rab.loading && !rab.data) body = <SkeletonRows count={3} label="Memuat RAB" />;
  else if (rab.error && !rab.data) {
    body = (
      <Notice
        tone="danger"
        title="RAB belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={rab.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(rab.error)}</p>
      </Notice>
    );
  } else {
    const saved = !!rab.data && rab.data.rows.length > 0 && !dirty;
    body = (
      <>
        <p className="wk-note">Alat hitung dari isian Anda: jumlah adalah volume dikali harga satuan. Tidak ada pajak atau margin yang ditambahkan diam-diam. Sesuaikan komponen harga dan pajak dengan dokumen pemilihan.</p>
        {rows.length === 0 ? (
          <p className="wk-empty">Belum ada item. Tambahkan baris untuk mulai menghitung.</p>
        ) : (
          <div className="wk-rab" role="table" aria-label="Rincian anggaran biaya">
            <div className="wk-rab-row wk-rab-headrow" role="row">
              <span role="columnheader">Uraian</span>
              <span role="columnheader">Volume</span>
              <span role="columnheader">Satuan</span>
              <span role="columnheader">Harga satuan (Rp)</span>
              <span role="columnheader" className="num">
                Jumlah
              </span>
              <span role="columnheader" className="wk-sr">
                Tindakan
              </span>
            </div>
            {parsed.map((r, i) => {
              const jumlah = Number.isNaN(r.v) || Number.isNaN(r.h) ? null : cents(r.v * r.h);
              return (
                <div key={r.key} className="wk-rab-row" role="row">
                  <span role="cell" className="wk-rab-uraian">
                    <input aria-label={`Uraian baris ${i + 1}`} value={r.uraian} maxLength={500} placeholder="Nama item pekerjaan atau barang" aria-invalid={!r.uraian.trim() || undefined} onChange={(e) => edit(r.key, { uraian: e.currentTarget.value })} />
                  </span>
                  <span role="cell" className="wk-rab-cell" data-label="Volume">
                    <input aria-label={`Volume baris ${i + 1}`} inputMode="decimal" value={r.volume} aria-invalid={Number.isNaN(r.v) || r.v < 0 || undefined} onChange={(e) => edit(r.key, { volume: e.currentTarget.value })} />
                  </span>
                  <span role="cell" className="wk-rab-cell" data-label="Satuan">
                    <input aria-label={`Satuan baris ${i + 1}`} value={r.satuan} maxLength={40} onChange={(e) => edit(r.key, { satuan: e.currentTarget.value })} />
                  </span>
                  <span role="cell" className="wk-rab-cell wk-rab-harga" data-label="Harga satuan (Rp)">
                    <input aria-label={`Harga satuan baris ${i + 1}`} inputMode="decimal" value={r.hargaSatuan} aria-invalid={Number.isNaN(r.h) || r.h < 0 || undefined} onChange={(e) => edit(r.key, { hargaSatuan: e.currentTarget.value })} />
                  </span>
                  <span role="cell" className="wk-rab-jumlah num">
                    {jumlah === null ? "Periksa angka" : formatRupiah(jumlah)}
                  </span>
                  <span role="cell" className="wk-rab-del">
                    <button
                      type="button"
                      className="ui-icon-btn"
                      aria-label={`Hapus baris ${i + 1}`}
                      title="Hapus"
                      onClick={() => (setRows((list) => list.filter((x) => x.key !== r.key)), setDirty(true))}
                    >
                      <Glyph name="trash" />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div className="wk-actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={rows.length >= 300}
            onClick={() => (setRows((list) => [...list, { key: localKey(), uraian: "", volume: "1", satuan: "unit", hargaSatuan: "0" }]), setDirty(true))}
          >
            <Glyph name="add" size={18} />
            <span>Tambah baris</span>
          </button>
        </div>
        <div className="wk-rab-foot">
          <Field id={`${uid}-ppn`} label="PPN (%)" hint={ppnInvalid ? undefined : "Kosongkan bila tidak dihitung di sini."} error={ppnInvalid ? "Isi angka 0 sampai 100." : null}>
            <input id={`${uid}-ppn`} inputMode="decimal" value={ppn} placeholder="Contoh: 11" aria-describedby={`${uid}-ppn-hint`} aria-invalid={ppnInvalid || undefined} onChange={(e) => (setPpn(e.currentTarget.value), setDirty(true))} />
          </Field>
          <dl className="wk-totals">
            <div>
              <dt>Subtotal</dt>
              <dd className="num">{formatRupiah(totals.subtotal)}</dd>
            </div>
            {ppnValue !== null && !ppnInvalid ? (
              <div>
                <dt>PPN {show(ppnValue)}%</dt>
                <dd className="num">{formatRupiah(totals.ppn)}</dd>
              </div>
            ) : null}
            <div className="wk-total">
              <dt>Total</dt>
              <dd className="num">{formatRupiah(totals.total)}</dd>
            </div>
          </dl>
        </div>
        <div className="wk-actions">
          <button type="button" className="btn btn-primary" disabled={!dirty || invalid || busy} onClick={() => void save()}>
            {busy ? "Menyimpan..." : "Simpan RAB"}
          </button>
          {saved ? (
            <>
              <a className="btn btn-secondary" href={Work.rabExportUrl(projectId, "xlsx")} download>
                <Glyph name="documentDownload" size={18} />
                <span>Unduh XLSX</span>
              </a>
              <a className="btn btn-secondary" href={Work.rabExportUrl(projectId, "docx")} download>
                <Glyph name="documentDownload" size={18} />
                <span>Unduh DOCX</span>
              </a>
            </>
          ) : (
            <span className="wk-hint">{invalid ? "Lengkapi uraian dan angka yang ditandai." : dirty ? "Simpan dulu agar unduhan memuat perubahan terakhir." : null}</span>
          )}
        </div>
      </>
    );
  }

  return (
    <WorkSection id="kalkulator-rab" title="Kalkulator RAB" lead="Rinci volume dan harga satuan penawaran. Jumlah dan total dihitung dari isian Anda.">
      {body}
      {error ? (
        <Notice tone="danger" title="RAB belum tersimpan">
          <p>{error}</p>
        </Notice>
      ) : null}
    </WorkSection>
  );
}
